import React, { useEffect, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import {
  CloseRounded,
  LocationOn,
  WarningAmberRounded,
  WheelchairPickup,
} from '@mui/icons-material'
import API from '../../../services/API'
import useIsMobile from '../../../helpers/useIsMobile'
import { getJourneyWorkflow, isRiskAssessmentJourney } from '../journeyWorkflow'
import JourneyTimesStrip from '../components/JourneyTimesStrip'
import { RiskChip, sectionDefinitions } from '../Patients/RiskAssessmentDialog'
import { describeOutstanding } from './crewJobForm'

// Read-only "everything the co-ordinator booked" view of one journey for the
// crew carrying it: both stops with their notes and escorts, the patient's
// mobility requirements, risk flags, allergies, medications and history, the
// booking's notes, and the patient's risk assessments. Works for any journey
// on one of the crew member's duties — current, upcoming or just finished.

const getPatient = (journey) => journey?.booking?.patient || journey?.patient

const getPatientName = (journey) => {
  const patient = getPatient(journey)
  return (
    [patient?.demographics?.firstName, patient?.demographics?.lastName]
      .filter(Boolean)
      .join(' ') || 'Unknown patient'
  )
}

const getJourneyTime = (journey) =>
  journey?.pickup?.when?.exactTime ||
  journey?.pickup?.when?.patientReadyFrom ||
  journey?.dropoff?.when?.exactTime ||
  journey?.dropoff?.when?.targetArrivalTime

const formatTime = (value) => (value ? moment(value).format('HH:mm') : '--:--')

const getFullAddress = (location) =>
  [
    location?.addressLine1,
    location?.addressLine2,
    location?.town,
    location?.city,
    location?.postCode,
  ]
    .filter(Boolean)
    .join(', ')

const getRiskFlags = (patient) => {
  const flags = patient?.riskFlags
  if (Array.isArray(flags)) return flags.filter(Boolean).map(String)
  if (flags && typeof flags === 'object') {
    return Object.entries(flags)
      .filter(([, value]) => value === true)
      .map(([key]) => key.replace(/([A-Z])/g, ' $1').trim())
  }
  return []
}

const asList = (value) =>
  (Array.isArray(value) ? value : [value])
    .map((item) => (typeof item === 'string' ? item.trim() : item))
    .filter(Boolean)

const Card = ({ title, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      borderRadius: 1,
      border: '1px solid #D8E3E0',
      bgcolor: '#fff',
    }}
  >
    <Stack spacing={1.25}>
      {title && (
        <Typography
          sx={{ color: '#0F766E', fontWeight: 900, fontSize: '0.9rem' }}
        >
          {title}
        </Typography>
      )}
      {children}
    </Stack>
  </Paper>
)

const Field = ({ label, value, children }) => {
  if (!children && (value === undefined || value === null || value === ''))
    return null
  return (
    <Box minWidth={0}>
      <Typography
        sx={{ fontSize: '0.68rem', color: '#64748B', fontWeight: 800 }}
      >
        {label.toUpperCase()}
      </Typography>
      {children || (
        <Typography
          sx={{
            color: '#0F172A',
            fontWeight: 700,
            fontSize: '0.92rem',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {value}
        </Typography>
      )}
    </Box>
  )
}

const FieldGrid = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
      gap: 1.25,
    }}
  >
    {children}
  </Box>
)

const NoteBlock = ({ label, text, tone = 'info' }) => {
  if (!text) return null
  const tones = {
    info: { bg: '#EFF6FF', border: '#BFDBFE', fg: '#1E3A8A' },
    warning: { bg: '#FFFBEB', border: '#FDE68A', fg: '#78350F' },
  }
  const colours = tones[tone] || tones.info
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1,
        bgcolor: colours.bg,
        border: `1px solid ${colours.border}`,
      }}
    >
      <Typography
        sx={{ fontSize: '0.68rem', fontWeight: 900, color: colours.fg }}
      >
        {label.toUpperCase()}
      </Typography>
      <Typography
        sx={{
          color: colours.fg,
          fontWeight: 600,
          fontSize: '0.92rem',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {text}
      </Typography>
    </Box>
  )
}

const StopDetails = ({ label, location, time, accent }) => (
  <Stack spacing={1}>
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box
        sx={{
          width: 42,
          minWidth: 42,
          height: 42,
          borderRadius: 1,
          bgcolor: accent,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LocationOn fontSize="small" />
      </Box>
      <Box minWidth={0} flex={1}>
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Typography sx={{ color: '#0F172A', fontWeight: 900 }}>
            {label}
          </Typography>
          <Typography sx={{ color: accent, fontWeight: 900 }}>
            {formatTime(time)}
          </Typography>
        </Stack>
        <Typography sx={{ color: '#334155', fontWeight: 800 }}>
          {[location?.name, location?.wardName].filter(Boolean).join(' · ') ||
            label}
        </Typography>
        {location?.wardLocation && (
          <Typography sx={{ color: '#475569', fontSize: '0.84rem' }}>
            {location.wardLocation}
          </Typography>
        )}
        <Typography sx={{ color: '#64748B', fontSize: '0.84rem' }}>
          {getFullAddress(location)}
        </Typography>
        {location?.contactNumber && (
          <Typography
            component="a"
            href={`tel:${location.contactNumber}`}
            sx={{
              color: '#0F766E',
              fontSize: '0.86rem',
              fontWeight: 800,
              textDecoration: 'none',
            }}
          >
            {location.contactNumber}
          </Typography>
        )}
      </Box>
    </Stack>
    <NoteBlock label={`${label} note`} text={location?.note} />
    {(location?.escorts || []).length > 0 && (
      <Box>
        <Typography
          sx={{ fontSize: '0.68rem', color: '#64748B', fontWeight: 800 }}
        >
          ESCORTS
        </Typography>
        {location.escorts.map((escort, index) => (
          <Typography
            key={escort._id || index}
            sx={{ color: '#0F172A', fontWeight: 700, fontSize: '0.9rem' }}
          >
            {[
              escort.name,
              escort.relationship && `(${escort.relationship})`,
              escort.mobilityCategory,
              escort.notes,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        ))}
      </Box>
    )}
  </Stack>
)

const ChipList = ({ items, color = '#334155', bg = '#F1F5F9' }) => (
  <Stack direction="row" gap={0.75} flexWrap="wrap">
    {items.map((item, index) => (
      <Chip
        key={`${item}-${index}`}
        label={item}
        size="small"
        sx={{
          borderRadius: 1,
          fontWeight: 800,
          bgcolor: bg,
          color,
          height: 'auto',
          '& .MuiChip-label': { whiteSpace: 'normal', py: 0.4 },
        }}
      />
    ))}
  </Stack>
)

const AssessmentCard = ({ assessment }) => {
  const requirements = assessment?.transportRequirements || {}
  const recommendations = assessment?.dischargeRecommendations || {}
  const ratedSections = sectionDefinitions.filter(([, key]) => {
    const section = assessment?.sections?.[key]
    return (
      section?.notes || (section?.rating && section.rating !== 'Not Assessed')
    )
  })
  const openActions = (assessment?.actions || []).filter(
    (action) => action?.action,
  )

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1,
        border: '1px solid #E2E8F0',
        bgcolor: '#F8FAFC',
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          <Box minWidth={0}>
            <Typography sx={{ fontWeight: 900, color: '#0F172A' }}>
              {assessment.assessmentType || 'Risk Assessment'}
              {assessment.status === 'Draft' ? ' (Draft)' : ''}
            </Typography>
            <Typography sx={{ color: '#64748B', fontSize: '0.78rem' }}>
              {[
                moment(assessment.visitDate || assessment.createdAt).format(
                  'DD/MM/YYYY',
                ),
                assessment.completedByName ||
                  [
                    assessment.completedBy?.firstName,
                    assessment.completedBy?.lastName,
                  ]
                    .filter(Boolean)
                    .join(' '),
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
          <RiskChip risk={assessment.overallRisk} />
        </Stack>

        <Field label="Summary" value={assessment.summary} />

        <FieldGrid>
          <Field label="Mobility" value={requirements.mobilityCategory} />
          <Field
            label="Crew requirements"
            value={requirements.crewRequirements}
          />
          <Field
            label="Vehicle requirements"
            value={requirements.vehicleRequirements}
          />
          <Field
            label="Equipment"
            value={asList(requirements.equipment).join(', ')}
          />
          {requirements.escortRequired && (
            <Field
              label="Escort"
              value={requirements.escortDetails || 'Required'}
            />
          )}
          {requirements.oxygenRequired && (
            <Field
              label="Oxygen"
              value={requirements.oxygenDetails || 'Required'}
            />
          )}
          <Field
            label="Bariatric considerations"
            value={requirements.bariatricConsiderations}
          />
        </FieldGrid>

        {ratedSections.length > 0 && (
          <Stack spacing={0.75}>
            {ratedSections.map(([label, key]) => {
              const section = assessment.sections[key]
              return (
                <Stack
                  key={key}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <Box sx={{ minWidth: 92 }}>
                    <RiskChip risk={section.rating} />
                  </Box>
                  <Box minWidth={0}>
                    <Typography
                      sx={{ fontWeight: 800, fontSize: '0.86rem' }}
                    >
                      {label}
                    </Typography>
                    {section.notes && (
                      <Typography
                        sx={{
                          color: '#475569',
                          fontSize: '0.84rem',
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {section.notes}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              )
            })}
          </Stack>
        )}

        <FieldGrid>
          <Field
            label="Safe to discharge home"
            value={
              recommendations.safeToDischargeHome &&
              recommendations.safeToDischargeHome !== 'Unknown'
                ? recommendations.safeToDischargeHome
                : ''
            }
          />
          <Field
            label="Recommended pathway"
            value={recommendations.recommendedPathway}
          />
          <Field
            label="Community support"
            value={recommendations.communitySupportRequired}
          />
        </FieldGrid>
        <Field
          label="Hospital team notes"
          value={recommendations.hospitalTeamNotes}
        />

        {openActions.length > 0 && (
          <Field label="Actions">
            {openActions.map((action, index) => (
              <Typography
                key={index}
                sx={{
                  color: '#0F172A',
                  fontWeight: 700,
                  fontSize: '0.88rem',
                  textDecoration: action.completed ? 'line-through' : 'none',
                }}
              >
                {[
                  action.action,
                  action.owner,
                  action.dueDate &&
                    `due ${moment(action.dueDate).format('DD/MM/YYYY')}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            ))}
          </Field>
        )}
      </Stack>
    </Box>
  )
}

const CrewJourneyDetails = ({ journey, open, onClose, onEditJob }) => {
  const isPhone = useIsMobile('sm')
  const [assessments, setAssessments] = useState([])
  const [assessmentsState, setAssessmentsState] = useState('idle')
  const journeyID = journey?._id

  useEffect(() => {
    if (!open || !journeyID) return undefined
    let cancelled = false
    setAssessments([])
    setAssessmentsState('loading')
    API.get(`/pts/crew/journeys/${journeyID}/risk-assessments`)
      .then((res) => {
        if (cancelled) return
        // API never rejects on 4xx/5xx — check the status
        if (res.status === 200) {
          setAssessments(res.data || [])
          setAssessmentsState('loaded')
        } else {
          setAssessmentsState('error')
        }
      })
      .catch(() => {
        if (!cancelled) setAssessmentsState('error')
      })
    return () => {
      cancelled = true
    }
  }, [open, journeyID])

  if (!journey) return null

  const patient = getPatient(journey)
  const demographics = patient?.demographics || {}
  const booking = journey.booking || {}
  const riskFlags = getRiskFlags(patient)
  const allergies = asList(patient?.allergies)
  const medications = asList(patient?.medications)
  const history = (patient?.pmhx || [])
    .map((entry) =>
      [entry?.disease, entry?.category && `(${entry.category})`]
        .filter(Boolean)
        .join(' '),
    )
    .filter(Boolean)
  const aids = asList(patient?.mobility?.aids)
  const visitOnly = isRiskAssessmentJourney(journey)
  const activityNotes = (booking.notes || []).filter((entry) => entry?.note)
  const age = demographics.dob
    ? moment().diff(moment(demographics.dob), 'years')
    : null
  const nextOfKin = [
    demographics.nokName,
    demographics.nokRelationship && `(${demographics.nokRelationship})`,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isPhone}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { bgcolor: '#EEF4F3' } }}
    >
      <Box sx={{ px: 2, py: 1.5, bgcolor: '#0F172A', color: '#fff' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box minWidth={0} flex={1}>
            <Typography sx={{ fontSize: '0.78rem', color: '#99F6E4' }}>
              {getJourneyWorkflow(journey.status, journey).stateLabel}
              {journey.journeyType || booking.journeyType
                ? ` · ${journey.journeyType || booking.journeyType}`
                : ''}
              {journey.priority && journey.priority !== 'Routine'
                ? ` · ${journey.priority}`
                : ''}
            </Typography>
            <Typography
              noWrap
              sx={{ fontSize: '1.3rem', fontWeight: 950, color: '#fff' }}
            >
              {getPatientName(journey)}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#CBD5E1' }}>
              {[booking.bookingRef, journey.journeyRef]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Close journey details"
            sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' }}
          >
            <CloseRounded />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 1.5 }}>
        <Stack spacing={1.5}>
          {/* A job the crew added themselves (self-dispatched duty) stays
              theirs to fill in until it closes */}
          {onEditJob && journey.crewJob?.editable && (
            <Alert
              severity={journey.crewJob.outstanding?.length ? 'warning' : 'info'}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => onEditJob(journey._id)}
                  sx={{ fontWeight: 900, whiteSpace: 'nowrap' }}
                >
                  {journey.crewJob.outstanding?.length
                    ? 'Add details'
                    : 'Edit job'}
                </Button>
              }
            >
              {journey.crewJob.outstanding?.length
                ? `Still needed to complete this job: ${describeOutstanding(journey.crewJob.outstanding)}.`
                : 'This job was added by the crew.'}
            </Alert>
          )}
          <Card title="Journey">
            <StopDetails
              label={visitOnly ? 'Visit Location' : 'Pickup'}
              location={journey.pickup}
              time={getJourneyTime(journey)}
              accent="#0F766E"
            />
            {!visitOnly && (
              <>
                <Divider />
                <StopDetails
                  label="Dropoff"
                  location={journey.dropoff}
                  time={
                    journey?.dropoff?.when?.targetArrivalTime ||
                    journey?.dropoff?.when?.exactTime
                  }
                  accent="#2563EB"
                />
              </>
            )}
            <JourneyTimesStrip journey={journey} />
            <FieldGrid>
              <Field
                label="Patient ready from"
                value={
                  journey?.pickup?.when?.patientReadyFrom
                    ? moment(journey.pickup.when.patientReadyFrom).format(
                        'DD/MM HH:mm',
                      )
                    : ''
                }
              />
              <Field label="Requested by" value={booking.organisation?.orgName} />
              <Field
                label="Requesting ward"
                value={[
                  booking.wardDepartment?.name,
                  booking.wardDepartment?.internalLocation,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
              <Field
                label="Ward contact"
                value={[
                  booking.wardDepartment?.contactName,
                  booking.wardDepartment?.contactPhone,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
              <Field label="Delay reason" value={journey.delayReason} />
            </FieldGrid>
          </Card>

          {(booking.bookingNotes || activityNotes.length > 0) && (
            <Card title="Booking notes">
              <NoteBlock
                label="Notes from the booking"
                text={booking.bookingNotes}
                tone="warning"
              />
              {activityNotes.map((entry, index) => (
                <Box key={entry._id || index}>
                  <Typography
                    sx={{
                      color: '#0F172A',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {entry.note}
                  </Typography>
                  <Typography sx={{ color: '#64748B', fontSize: '0.76rem' }}>
                    {[
                      entry.createdAt &&
                        moment(entry.createdAt).format('DD/MM/YYYY HH:mm'),
                      [entry.author?.firstName, entry.author?.lastName]
                        .filter(Boolean)
                        .join(' '),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
              ))}
            </Card>
          )}

          <Card title="Patient">
            <FieldGrid>
              <Field
                label="Date of birth"
                value={
                  demographics.dob
                    ? `${moment(demographics.dob).format('DD/MM/YYYY')} (${age})`
                    : 'Unknown'
                }
              />
              <Field label="Gender" value={demographics.gender} />
              <Field label="Patient ID" value={patient?.patientID} />
              <Field label="NHS number" value={demographics.nhsNumber} />
              <Field
                label="Phone"
                value={[demographics.phoneNumber1, demographics.phoneNumber2]
                  .filter(Boolean)
                  .join(', ')}
              />
              <Field label="GP surgery" value={demographics.gpSurgery} />
            </FieldGrid>
            <Field label="Home address" value={getFullAddress(demographics)} />
            <FieldGrid>
              <Field label="Next of kin" value={nextOfKin} />
              <Field
                label="Next of kin contact"
                value={demographics.nokContactNumber}
              />
            </FieldGrid>
          </Card>

          <Card title="Mobility & requirements">
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              <Chip
                icon={<WheelchairPickup />}
                label={patient?.mobility?.category || 'Mobility not recorded'}
                sx={{ borderRadius: 1, bgcolor: '#F8FAFC', fontWeight: 800 }}
              />
              {patient?.mobility?.isBariatric && (
                <Chip
                  label="Bariatric"
                  color="warning"
                  sx={{ borderRadius: 1, fontWeight: 800 }}
                />
              )}
              {patient?.mobility?.mustTravelAlone && (
                <Chip
                  label="Must travel alone"
                  color="warning"
                  sx={{ borderRadius: 1, fontWeight: 800 }}
                />
              )}
            </Stack>
            {aids.length > 0 && (
              <Field label="Mobility aids">
                <ChipList items={aids} />
              </Field>
            )}
            {riskFlags.length > 0 && (
              <Field label="Risks & requirements">
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  {riskFlags.map((flag) => (
                    <Chip
                      key={flag}
                      icon={<WarningAmberRounded />}
                      label={flag}
                      color="error"
                      size="small"
                      sx={{
                        fontWeight: 800,
                        borderRadius: 1,
                        height: 'auto',
                        '& .MuiChip-label': { whiteSpace: 'normal', py: 0.4 },
                      }}
                    />
                  ))}
                </Stack>
              </Field>
            )}
          </Card>

          <Card title="Clinical">
            <NoteBlock label="Patient notes" text={patient?.clinicalNotes} />
            <Field label="Allergies">
              {allergies.length ? (
                <ChipList items={allergies} color="#991B1B" bg="#FEE2E2" />
              ) : (
                <Typography sx={{ color: '#64748B', fontSize: '0.88rem' }}>
                  None recorded
                </Typography>
              )}
            </Field>
            <Field label="Medications">
              {medications.length ? (
                <ChipList items={medications} />
              ) : (
                <Typography sx={{ color: '#64748B', fontSize: '0.88rem' }}>
                  None recorded
                </Typography>
              )}
            </Field>
            <Field label="Past medical history">
              {history.length ? (
                <ChipList items={history} />
              ) : (
                <Typography sx={{ color: '#64748B', fontSize: '0.88rem' }}>
                  None recorded
                </Typography>
              )}
            </Field>
          </Card>

          <Card title="Risk assessments">
            {assessmentsState === 'loading' && (
              <Box display="flex" justifyContent="center" py={1}>
                <CircularProgress size={22} />
              </Box>
            )}
            {assessmentsState === 'error' && (
              <Alert severity="warning">
                Unable to load this patient&apos;s risk assessments.
              </Alert>
            )}
            {assessmentsState === 'loaded' && !assessments.length && (
              <Typography sx={{ color: '#64748B', fontSize: '0.88rem' }}>
                No risk assessments recorded for this patient.
              </Typography>
            )}
            {assessments.map((assessment) => (
              <AssessmentCard key={assessment._id} assessment={assessment} />
            ))}
          </Card>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default CrewJourneyDetails
